import { navigate, routes } from '@redwoodjs/router'

/**
 * Checks if a notification is from a different company than the current user's company
 */
export const isNotificationFromDifferentCompany = (
  notification: { company?: { companyId?: string } },
  currentUserCompanyId?: string
): boolean => {
  return (
    !!notification?.company &&
    currentUserCompanyId !== notification?.company?.companyId
  )
}

/**
 * Gets the tooltip message for actions that require switching companies
 */
export const getSwitchCompanyTooltip = (companyName?: string): string => {
  return `Switch to ${companyName || 'this company'} to view this content`
}

/**
 * Gets the button text for actions that require switching companies
 */
export const getSwitchCompanyButtonText = (
  isFromDifferentCompany: boolean,
  defaultText: string
): string => {
  return isFromDifferentCompany ? `Switch & ${defaultText}` : defaultText
}

/**
 * Title written by the VoE post-call handler
 * (`VOE_AGENT_CALL_NOTIFICATION_TITLE` in handleVoEPostCall).
 */
export const VOE_AGENT_CALL_NOTIFICATION_TITLE = 'VoE agent call completed'

type VoENotificationForRoute = {
  title?: string | null
  activityLogId?: string | null
  applicant?: { applicantId?: string | null } | null
  voe?: { voeId?: string | null } | null
  activityLog?: {
    activityLogId?: string | null
    voiceAgent?: { voiceAgentId?: string | null } | null
  } | null
}

const callIdentifiers = (notification: VoENotificationForRoute) => ({
  activityLogId:
    notification.activityLog?.activityLogId ||
    notification.activityLogId ||
    undefined,
  voiceAgentId: notification.activityLog?.voiceAgent?.voiceAgentId || undefined,
})

/**
 * Agent-call notifications carry the call's activity log (and the fixed
 * title). They open the same VoE drawer as every other VoE notification,
 * plus the call itself.
 */
const isVoEAgentCallNotification = (notification: VoENotificationForRoute) => {
  const { activityLogId } = callIdentifiers(notification)
  return (
    notification.title === VOE_AGENT_CALL_NOTIFICATION_TITLE ||
    Boolean(notification.voe?.voeId && activityLogId)
  )
}

/**
 * Deep-link for a VoE notification. With an applicant and a VoE, every
 * notification opens the applicant's VoE tab with that VoE's drawer, so the
 * destination is the same whether or not the employer has answered
 * (ENG-4867). Agent calls also keep the call / voice-agent ids so the call
 * opens alongside. Without an applicant, fall back to the standalone VoE
 * request, then the applicant's VoE tab.
 */
export const getVoENotificationRoute = (
  notification: VoENotificationForRoute
): string | null => {
  const applicantId = notification.applicant?.applicantId
  const voeId = notification.voe?.voeId
  const { activityLogId, voiceAgentId } = callIdentifiers(notification)

  if (isVoEAgentCallNotification(notification)) {
    if (applicantId && voeId) {
      return routes.voeResponseReview({
        id: applicantId,
        tab: 'voe',
        voeId,
        ...(activityLogId ? { activityLogId } : {}),
        ...(voiceAgentId ? { voiceAgentId } : {}),
      })
    }

    if (activityLogId) {
      return routes.communicationsWithId({
        id: activityLogId,
        ...(voiceAgentId ? { voiceAgents: voiceAgentId } : {}),
      })
    }
  }

  if (applicantId && voeId) {
    return routes.voeResponseReview({
      id: applicantId,
      tab: 'voe',
      voeId,
    })
  }

  if (voeId) {
    return routes.voeRequestDetails({ id: voeId })
  }

  if (applicantId) {
    return routes.applicantDetails({ id: applicantId, tab: 'voe' })
  }

  return null
}

/**
 * Handles navigation with automatic company switching if needed
 */
export const handleNotificationNavigation = (
  route: string,
  isFromDifferentCompany: boolean,
  employeeId?: string,
  updateCompanyAndRedirect?: (
    employeeId: string,
    route: string
  ) => Promise<void>
) => {
  if (isFromDifferentCompany && employeeId && updateCompanyAndRedirect) {
    updateCompanyAndRedirect(employeeId, route)
  } else {
    navigate(route)
  }
}
